import { collection, doc, addDoc, updateDoc, deleteDoc, query, where, orderBy, onSnapshot, serverTimestamp, getDocs, limit } from 'backend/database';;
import { db } from '../backend/config';

class CallService {
  constructor() {
    this.activeCall = null;
    this.callListeners = new Map();
  }

  // Generate unique call ID
  generateCallId() {
    return `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Initiate a call
  // Supports both object parameter and separate parameters for backward compatibility
  async initiateCall(callerIdOrOptions, recipientId = null, callType = 'video') {
    try {
      let callerId, finalRecipientId, finalCallType, callerName, recipientName;
      
      // Check if first parameter is an object (new format)
      if (typeof callerIdOrOptions === 'object' && callerIdOrOptions !== null) {
        callerId = callerIdOrOptions.callerId;
        finalRecipientId = callerIdOrOptions.recipientId;
        finalCallType = callerIdOrOptions.callType || 'video';
        callerName = callerIdOrOptions.callerName;
        recipientName = callerIdOrOptions.recipientName;
      } else {
        // Old format: separate parameters
        callerId = callerIdOrOptions;
        finalRecipientId = recipientId;
        finalCallType = callType;
      }
      
      console.log('🔥 CallService.initiateCall called:', { 
        callerId, 
        recipientId: finalRecipientId, 
        callType: finalCallType,
        callerName,
        recipientName
      });
      
      // Validation
      if (!callerId || !finalRecipientId) {
        const errorMsg = `Caller ID and Recipient ID are required. Got callerId: ${callerId}, recipientId: ${finalRecipientId}`;
        console.error('❌ Validation failed:', errorMsg);
        throw new Error(errorMsg);
      }
      
      if (callerId === finalRecipientId) {
        throw new Error('Cannot call yourself');
      }
      
      const callId = this.generateCallId();
      const callData = {
        callId,
        callerId,
        recipientId: finalRecipientId,
        callType: finalCallType,
        callerName: callerName || null,
        recipientName: recipientName || null,
        status: 'initiating',
        createdAt: serverTimestamp(),
        answeredAt: null,
        endedAt: null,
        duration: 0
      };

      // Create call document — the backend atomically creates the callee's
      // 'incoming' call_notifications row in this same request, so the ring
      // goes out in one round-trip instead of two.
      console.log('📄 Creating call document in Database...');
      const callDoc = await addDoc(collection(db, 'calls'), callData);
      console.log('✅ Call document created:', callDoc.id);

      // Fire-and-forget: newer backends already created the 'incoming'
      // notification inside the calls POST (the server dedupes this), but
      // keeping the POST preserves the ring if the app is ever pointed at
      // an older backend. Not awaited — it's off the ring critical path.
      console.log('🔔 Sending call notification to recipient:', finalRecipientId);
      this.sendCallNotification(finalRecipientId, {
        callId,
        callerId,
        callType: finalCallType,
        status: 'incoming',
        callerName: callerName || null
      }).catch(err => console.warn('Call notification fallback failed:', err));

      this.activeCall = { ...callData, id: callDoc.id };

      return { 
        success: true, 
        callId, 
        callData: this.activeCall,
        signalingRef: callDoc // Return doc reference for WebRTC signaling
      };
    } catch (error) {
      console.error('❌ Error initiating call:', error);
      throw error; // Re-throw to allow caller to handle
    }
  }

  // Ring the other party of a scheduled consultation. The calls row's
  // call_id is `consult_<appointmentId>__<ts>`; the backend creates the
  // callee's 'incoming' notification on insert, and accepting routes into the
  // consult screen instead of the generic chat-call WebRTC flow.
  async ringConsultation({ appointmentId, callerId, recipientId, callType = 'video', callerName, recipientName }) {
    const callId = `consult_${appointmentId}__${Date.now()}`;
    await addDoc(collection(db, 'calls'), {
      callId,
      callerId,
      recipientId,
      callType,
      callerName: callerName || null,
      recipientName: recipientName || null,
      status: 'initiating',
      createdAt: serverTimestamp(),
      answeredAt: null,
      endedAt: null,
      duration: 0
    });
    return callId;
  }

  // Answer a call — lookups and writes are parallelized so the caller sees
  // the 'answered' flip in ~2 round-trips instead of ~5 on slow links.
  async answerCall(callId, recipientId) {
    try {
      const callsQuery = query(
        collection(db, 'calls'),
        where('callId', '==', callId)
      );
      const notificationsQuery = query(
        collection(db, 'callNotifications'),
        where('callId', '==', callId),
        where('userId', '==', recipientId),
        where('status', '==', 'incoming')
      );

      const [snapshot, notificationsSnapshot] = await Promise.all([
        getDocs(callsQuery),
        getDocs(notificationsQuery)
      ]);
      if (snapshot.empty) {
        throw new Error('Call not found');
      }

      const callDoc = snapshot.docs[0];
      await Promise.all([
        updateDoc(callDoc.ref, {
          status: 'answered',
          answeredAt: serverTimestamp()
        }),
        // Update the original incoming call notification to answered
        ...notificationsSnapshot.docs.map(doc =>
          updateDoc(doc.ref, {
            status: 'answered',
            updatedAt: serverTimestamp()
          })
        ),
        // Send answer notification to caller
        this.sendCallNotification(callDoc.data().callerId, {
          callId,
          recipientId,
          status: 'answered'
        })
      ]);
      console.log(`✅ Updated ${notificationsSnapshot.size} call notifications to answered`);

      return { success: true };
    } catch (error) {
      console.error('Error answering call:', error);
      return { success: false, error: error.message };
    }
  }

  // Reject a call
  async rejectCall(callId, recipientId) {
    try {
      const callsQuery = query(
        collection(db, 'calls'),
        where('callId', '==', callId)
      );

      const notificationsQuery = query(
        collection(db, 'callNotifications'),
        where('callId', '==', callId),
        where('userId', '==', recipientId),
        where('status', '==', 'incoming')
      );

      const [snapshot, notificationsSnapshot] = await Promise.all([
        getDocs(callsQuery),
        getDocs(notificationsQuery)
      ]);
      if (snapshot.empty) {
        throw new Error('Call not found');
      }

      const callDoc = snapshot.docs[0];
      await Promise.all([
        updateDoc(callDoc.ref, {
          status: 'rejected',
          endedAt: serverTimestamp()
        }),
        // Update the original incoming call notification to rejected
        ...notificationsSnapshot.docs.map(doc =>
          updateDoc(doc.ref, {
            status: 'rejected',
            updatedAt: serverTimestamp()
          })
        ),
        // Send rejection notification to caller
        this.sendCallNotification(callDoc.data().callerId, {
          callId,
          recipientId,
          status: 'rejected'
        })
      ]);
      console.log(`✅ Updated ${notificationsSnapshot.size} call notifications to rejected`);

      return { success: true };
    } catch (error) {
      console.error('Error rejecting call:', error);
      return { success: false, error: error.message };
    }
  }

  // End a call
  async endCall(callId, duration = 0) {
    try {
      const callsQuery = query(
        collection(db, 'calls'),
        where('callId', '==', callId)
      );

      const snapshot = await getDocs(callsQuery);
      if (snapshot.empty) {
        throw new Error('Call not found');
      }

      const callDoc = snapshot.docs[0];
      const callData = callDoc.data();
      
      await updateDoc(callDoc.ref, {
        status: 'ended',
        endedAt: serverTimestamp(),
        duration: duration
      });

      // Update all call notifications for this call to ended
      const notificationsQuery = query(
        collection(db, 'callNotifications'),
        where('callId', '==', callId),
        where('status', '==', 'incoming')
      );
      
      const notificationsSnapshot = await getDocs(notificationsQuery);
      const updatePromises = notificationsSnapshot.docs.map(doc => 
        updateDoc(doc.ref, {
          status: 'ended',
          updatedAt: serverTimestamp()
        })
      );
      await Promise.all(updatePromises);
      console.log(`✅ Updated ${notificationsSnapshot.size} call notifications to ended`);

      // Clean up active call
      if (this.activeCall && this.activeCall.callId === callId) {
        this.activeCall = null;
      }

      return { success: true };
    } catch (error) {
      console.error('Error ending call:', error);
      return { success: false, error: error.message };
    }
  }

  // Send call notification
  async sendCallNotification(userId, notificationData) {
    try {
      console.log('📨 sendCallNotification called:', { userId, notificationData });
      
      // Only send to callNotifications collection for now to avoid permission issues
      const notificationDoc = await addDoc(collection(db, 'callNotifications'), {
        userId,
        ...notificationData,
        timestamp: serverTimestamp()
      });
      
      console.log('✅ Notification document created:', {
        notificationId: notificationDoc.id,
        userId,
        callId: notificationData.callId,
        status: notificationData.status
      });
    } catch (error) {
      console.error('❌ Error sending call notification:', error);
      throw error; // Re-throw to let caller know it failed
    }
  }

  // Listen for incoming calls
  listenForIncomingCalls(userId, onIncomingCall) {
    const notificationsQuery = query(
      collection(db, 'callNotifications'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc')
    );

    const shownCallIds = new Set();

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const notification = change.doc.data();
          const callId = change.doc.id;

          // Skip calls we've already shown to prevent duplicates on re-mount
          if (shownCallIds.has(callId)) return;
          shownCallIds.add(callId);

          // Only show incoming calls that are:
          // 1. Status is 'incoming'
          // 2. Created within the last 2 minutes (to avoid showing old calls on refresh)
          if (notification.status === 'incoming') {
            const notificationTime = notification.timestamp?.toDate?.() || new Date(notification.timestamp);
            const now = new Date();
            // An unparseable timestamp yields NaN — treat it as fresh rather
            // than silently expiring a live incoming call.
            const timeDiff = isNaN(notificationTime.getTime())
              ? 0
              : (now - notificationTime) / 1000; // seconds

            // Only show if call is less than 2 minutes old
            if (timeDiff < 120) {
              console.log('Valid incoming call (age:', Math.floor(timeDiff), 'seconds)');
              onIncomingCall(notification);
            } else {
              console.log('Ignoring old call notification (age:', Math.floor(timeDiff), 'seconds)');
              // Clean up old notification
              this.updateCallNotificationStatus(change.doc.id, 'expired').catch(err => {
                console.error('Failed to clean up old notification:', err);
              });
            }
          }
        }
      });
    });

    this.callListeners.set(userId, unsubscribe);
    return unsubscribe;
  }

  // Stop listening for calls
  stopListeningForCalls(userId) {
    const unsubscribe = this.callListeners.get(userId);
    if (unsubscribe) {
      unsubscribe();
      this.callListeners.delete(userId);
    }
  }

  // Update call notification status
  async updateCallNotificationStatus(notificationId, status) {
    try {
      const notificationRef = doc(db, 'callNotifications', notificationId);
      await updateDoc(notificationRef, {
        status: status,
        updatedAt: serverTimestamp()
      });
      console.log(`✅ Call notification ${notificationId} updated to ${status}`);
    } catch (error) {
      console.error('Error updating call notification status:', error);
      throw error;
    }
  }

  // Get call history for a user
  async getCallHistory(userId, limit = 50) {
    try {
      const callsQuery = query(
        collection(db, 'calls'),
        where('callerId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limit)
      );

      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(callsQuery, (snapshot) => {
          const calls = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          resolve(calls);
        }, reject);

        // Auto-unsubscribe after 5 seconds to prevent memory leaks
        setTimeout(() => unsubscribe(), 5000);
      });
    } catch (error) {
      console.error('Error getting call history:', error);
      throw error;
    }
  }

  // Get recent calls (both incoming and outgoing)
  async getRecentCalls(userId, limit = 20) {
    try {
      // Get outgoing calls
      const outgoingQuery = query(
        collection(db, 'calls'),
        where('callerId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limit)
      );

      // Get incoming calls
      const incomingQuery = query(
        collection(db, 'calls'),
        where('recipientId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limit)
      );

      const [outgoingSnapshot, incomingSnapshot] = await Promise.all([
        getDocs(outgoingQuery),
        getDocs(incomingQuery)
      ]);

      const outgoingCalls = outgoingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        direction: 'outgoing'
      }));

      const incomingCalls = incomingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        direction: 'incoming'
      }));

      // Combine and sort by creation time
      const allCalls = [...outgoingCalls, ...incomingCalls]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, limit);

      return allCalls;
    } catch (error) {
      console.error('Error getting recent calls:', error);
      throw error;
    }
  }

  // Update call status
  async updateCallStatus(callId, status) {
    try {
      const callsQuery = query(
        collection(db, 'calls'),
        where('callId', '==', callId)
      );

      const snapshot = await getDocs(callsQuery);
      if (snapshot.empty) {
        console.warn('Call not found:', callId);
        return false;
      }

      const callDoc = snapshot.docs[0];
      await updateDoc(callDoc.ref, {
        status: status,
        updatedAt: serverTimestamp()
      });

      console.log(`✅ Call ${callId} status updated to:`, status);
      return true;
    } catch (error) {
      console.error('Error updating call status:', error);
      throw error;
    }
  }

  // Get calls for a user with filter: 'all' | 'missed' | 'received' | 'outgoing'
  async getUserCalls(userId, filter = 'all', limitCount = 50) {
    try {
      // Fetch both directions
      const outgoingQuery = query(
        collection(db, 'calls'),
        where('callerId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const incomingQuery = query(
        collection(db, 'calls'),
        where('recipientId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const [outgoingSnapshot, incomingSnapshot] = await Promise.all([
        getDocs(outgoingQuery),
        getDocs(incomingQuery)
      ]);

      const outgoing = outgoingSnapshot.docs.map(doc => ({ id: doc.id, direction: 'outgoing', ...doc.data() }));
      const incoming = incomingSnapshot.docs.map(doc => ({ id: doc.id, direction: 'incoming', ...doc.data() }));

      let combined = [...outgoing, ...incoming].sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));

      if (filter === 'missed') {
        combined = combined.filter(c => (c.direction === 'incoming') && (c.status === 'initiating' || c.status === 'rejected' || c.status === 'missed'));
      } else if (filter === 'received') {
        combined = combined.filter(c => (c.direction === 'incoming') && c.status === 'answered');
      } else if (filter === 'outgoing') {
        combined = combined.filter(c => c.direction === 'outgoing');
      }

      return combined.slice(0, limitCount);
    } catch (error) {
      console.error('Error getting user calls:', error);
      return [];
    }
  }

  // Get call statistics
  async getCallStats(userId, days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const callsQuery = query(
        collection(db, 'calls'),
        where('callerId', '==', userId),
        where('createdAt', '>=', startDate)
      );

      const snapshot = await getDocs(callsQuery);
      const calls = snapshot.docs.map(doc => doc.data());

      const stats = {
        totalCalls: calls.length,
        answeredCalls: calls.filter(call => call.status === 'answered').length,
        missedCalls: calls.filter(call => call.status === 'rejected' || call.status === 'initiating' || call.status === 'missed').length,
        totalDuration: calls.reduce((total, call) => total + (call.duration || 0), 0),
        averageDuration: 0,
        videoCalls: calls.filter(call => call.callType === 'video').length,
        voiceCalls: calls.filter(call => call.callType === 'audio').length
      };

      if (stats.answeredCalls > 0) {
        stats.averageDuration = stats.totalDuration / stats.answeredCalls;
      }

      return stats;
    } catch (error) {
      console.error('Error getting call stats:', error);
      throw error;
    }
  }

  // Clean up old notifications
  async cleanupOldNotifications(daysOld = 7) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const notificationsQuery = query(
        collection(db, 'callNotifications'),
        where('timestamp', '<', cutoffDate)
      );

      const snapshot = await getDocs(notificationsQuery);
      const batch = [];
      
      snapshot.docs.forEach(doc => {
        batch.push(deleteDoc(doc.ref));
      });

      await Promise.all(batch);
      return { success: true, deletedCount: batch.length };
    } catch (error) {
      console.error('Error cleaning up notifications:', error);
      return { success: false, error: error.message };
    }
  }

  // Clean up signaling records for a completed call
  async cleanupSignalingRecords(callId) {
    try {
      if (!callId) return;
      const signalingQuery = query(
        collection(db, 'signaling'),
        where('callId', '==', callId)
      );
      const snapshot = await getDocs(signalingQuery);
      const batch = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(batch);
      console.log(`🧹 Cleaned up ${snapshot.size} signaling records for call ${callId}`);
    } catch (error) {
      console.error('Error cleaning up signaling records:', error);
    }
  }

  // Clean up call notification records for a completed call
  async cleanupCallNotifications(callId) {
    try {
      if (!callId) return;
      const notificationsQuery = query(
        collection(db, 'callNotifications'),
        where('callId', '==', callId)
      );
      const snapshot = await getDocs(notificationsQuery);
      const batch = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(batch);
      console.log(`🧹 Cleaned up ${snapshot.size} call notifications for call ${callId}`);
    } catch (error) {
      console.error('Error cleaning up call notifications:', error);
    }
  }

  // Get active call
  getActiveCall() {
    return this.activeCall;
  }

  // Set active call
  setActiveCall(call) {
    this.activeCall = call;
  }

  // Clear active call
  clearActiveCall() {
    this.activeCall = null;
  }

  // Listen for WebRTC signaling messages
  listenForSignaling(callId, onSignalingMessage) {
    const signalingQuery = query(
      collection(db, 'signaling'),
      where('callId', '==', callId),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(signalingQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const message = change.doc.data();
          console.log('📡 Received signaling message:', message.type);
          onSignalingMessage({
            ...message,
            documentId: change.doc.id
          });
        }
      });
    });

    return unsubscribe;
  }

  // Send SDP offer/answer for WebRTC
  async sendSdpMessage(callId, type, sdp, fromUserId) {
    try {
      console.log(`📨 Sending SDP ${type} for call:`, callId);
      
      const messageDoc = await addDoc(collection(db, 'signaling'), {
        callId,
        type,
        sdp: typeof sdp === 'object' ? sdp.sdp : sdp,
        from: fromUserId,
        timestamp: serverTimestamp()
      });

      console.log(`✅ SDP ${type} sent successfully`);
      return messageDoc.id;
    } catch (error) {
      console.error(`❌ Error sending SDP ${type}:`, error);
      throw error;
    }
  }

  // Send ICE candidate for WebRTC
  async sendIceCandidate(callId, candidate, fromUserId) {
    try {
      if (!candidate || !candidate.candidate) {
        return; // Skip null candidates
      }

      const candidateDoc = await addDoc(collection(db, 'signaling'), {
        callId,
        type: 'ice-candidate',
        candidate: {
          candidate: candidate.candidate,
          sdpMLineIndex: candidate.sdpMLineIndex,
          sdpMid: candidate.sdpMid,
          usernameFragment: candidate.usernameFragment
        },
        from: fromUserId,
        timestamp: serverTimestamp()
      });

      return candidateDoc.id;
    } catch (error) {
      console.error('❌ Error sending ICE candidate:', error);
      throw error;
    }
  }

  // Clean up signaling messages after call ends
  async cleanupSignalingMessages(callId) {
    try {
      const signalingQuery = query(
        collection(db, 'signaling'),
        where('callId', '==', callId)
      );

      const snapshot = await getDocs(signalingQuery);
      const batch = [];
      
      snapshot.docs.forEach(doc => {
        batch.push(deleteDoc(doc.ref));
      });

      await Promise.all(batch);
      console.log(`✅ Cleaned up ${batch.length} signaling messages for call: ${callId}`);
      return { success: true, cleanedCount: batch.length };
    } catch (error) {
      console.error('Error cleaning up signaling messages:', error);
      return { success: false, error: error.message };
    }
  }

  // Check if another participant is online
  async isParticipantOnline(userId) {
    try {
      // Check for recent listener activity
      const notificationsQuery = query(
        collection(db, 'callNotifications'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(1)
      );

      const snapshot = await getDocs(notificationsQuery);
      
      if (snapshot.empty) {
        return false;
      }

      // If they have a notification listener, they're likely online
      const lastNotificationTime = snapshot.docs[0].data().timestamp?.toDate?.() || new Date(0);
      const now = new Date();
      const diffMinutes = (now - lastNotificationTime) / (1000 * 60);

      // Consider online if activity within 5 minutes
      return diffMinutes < 5;
    } catch (error) {
      console.error('Error checking participant online status:', error);
      return false;
    }
  }
}

export default CallService;
